import { useState } from 'react';
import { BtnStatus } from '../../../../../Js/React/UI/BizButton/BizButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	clearPublicRegistrationLink,
	createUnit,
	getAllUnits,
	getAllowedDomainsForPublicRegistrationLink,
	getPublicRegistrationLink,
	getRootUnits,
	getUnit,
	getUnitTypes,
	getUnitsByParent,
	updateUnit,
} from '../../Services/admin/unit';
import { Unit } from '../../Types/unit';
import { AccessTypeKey } from '../../Types/access';

const QUERYKEY_ROOTUNITS = 'ROOTUNITS';
const QUERYKEY_UNITS_BY_PARENT = 'UNITSBYPARENT';
const QUERYKEY_UNITS_ALL = 'UNITSALL';
const QUERYKEY_UNITS_POSSIBLE_PARENTS = 'UNITS_POSSIBLE_PARENTS';
const QUERYKEY_UNIT = 'UNIT';
const QUERYKEY_UNITTYPES = 'UNITTYPES';
const QUERYKEY_ALLOWED_DOMAINS_FOR_PUBLIC_REGISTRATION_LINK = 'ALLOWEDDOMAINSFORPUBLICREGISTRATIONLINK';

export const useAllUnits = (
	accessTypeKey: AccessTypeKey | null,
	includeInactive?: boolean,
	overrideLimitedAccessMode?: boolean
) => {
	const { data: units } = useQuery({
		queryKey: [QUERYKEY_UNITS_ALL, accessTypeKey, includeInactive, overrideLimitedAccessMode],
		queryFn: async () => {
			const data = await getAllUnits(accessTypeKey, includeInactive, overrideLimitedAccessMode);
			return data?.sort((a, b) => (a.Name > b.Name ? 1 : -1));
		},
	});
	return units;
};

export const useGetPossibleParentUnits = (accessTypeKey: AccessTypeKey | null) => {
	const { data: units } = useQuery({
		queryKey: [QUERYKEY_UNITS_POSSIBLE_PARENTS],
		queryFn: async () => {
			const data = await getAllUnits(accessTypeKey, false);
			return data?.filter((u) => u.UnitType?.Settings?.AllowChildren);
		},
	});
	return units;
};

export const useRootUnits = (accessTypeKey?: AccessTypeKey) => {
	const { data: rootUnits } = useQuery({
		queryKey: [QUERYKEY_ROOTUNITS, accessTypeKey],
		queryFn: async () => {
			const data = await getRootUnits(accessTypeKey);
			return data;
		},
	});
	return rootUnits;
};

export const useUnitsByParent = (parentId: number) => {
	const { data: units } = useQuery({
		queryKey: [QUERYKEY_UNITS_BY_PARENT, parentId],
		queryFn: async () => {
			const data = await getUnitsByParent(parentId);
			return data;
		},
	});
	return units;
};

export const useUnitTypes = () => {
	const { data: unitTypes } = useQuery({
		queryKey: [QUERYKEY_UNITTYPES],
		queryFn: async () => {
			const data = await getUnitTypes();
			return data;
		},
	});
	return unitTypes;
};

export const useUnit = (id: number | undefined) => {
	const { data: unit } = useQuery({
		queryKey: [QUERYKEY_UNIT, id],
		queryFn: async () => {
			if (id) {
				const data = await getUnit(id);
				return data;
			}
		},
		enabled: !isNaN(Number(id)) && Number(id) > 0,
	});
	return unit;
};

export const useAllowedDomainsForPublicRegistrationLink = (unitId: number) => {
	const { data: allowedDomains } = useQuery({
		queryKey: [QUERYKEY_ALLOWED_DOMAINS_FOR_PUBLIC_REGISTRATION_LINK, unitId],
		queryFn: async () => {
			const domains = await getAllowedDomainsForPublicRegistrationLink(unitId);
			return domains;
		},
	});
	return allowedDomains;
};

export const usePublicRegistrationLink = () => {
	const queryClient = useQueryClient();
	const [generateStatus, setGenerateStatus] = useState<BtnStatus>('INITIAL');

	const generateLinkMutation = useMutation({
		mutationFn: (options: { unitId: number; forceRegenerate: boolean }) => {
			return getPublicRegistrationLink(options.unitId, options.forceRegenerate);
		},
		onMutate: () => {
			setGenerateStatus('SAVING');
		},
		onSuccess: (result, options) => {
			queryClient.setQueryData([QUERYKEY_UNIT, options.unitId], (oldUnit: Unit | undefined) => {
				if (oldUnit && result?.url && result.expiresUtc) {
					console.log('result', result);
					return {
						...oldUnit,
						PublicRegistrationUrl: result.url,
						PublicRegistrationUrlExpiresUtc: result.expiresUtc,
					};
				}
				return oldUnit;
			});
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ALLOWED_DOMAINS_FOR_PUBLIC_REGISTRATION_LINK, options.unitId] });
			setGenerateStatus('SAVED');
		},
		onError: (err) => {
			setGenerateStatus('ERROR');
			console.log(err);
		},
	});

	const clearLinkMutation = useMutation({
		mutationFn: (unitId: number) => {
			return clearPublicRegistrationLink(unitId);
		},
		onSuccess: (result, unitId) => {
			if (result) {
				queryClient.invalidateQueries({ queryKey: [QUERYKEY_UNIT, unitId] });
				queryClient.invalidateQueries({ queryKey: [QUERYKEY_ALLOWED_DOMAINS_FOR_PUBLIC_REGISTRATION_LINK, unitId] });
			}
		},
		onError: (err) => console.log(err),
	});

	return {
		generateLink: (unitId: number, forceRegenerate: boolean) => generateLinkMutation.mutate({ unitId, forceRegenerate }),
		clearLink: (unitId: number) => clearLinkMutation.mutate(unitId),
		generateStatus,
	};
};

export const useSaveUnit = () => {
	const queryClient = useQueryClient();
	const [saveStatus, setSaveStatus] = useState<BtnStatus>('INITIAL');
	const [createdId, setCreatedId] = useState<number | null>(null);

	const saveUnitMutation = useMutation({
		mutationFn: (unit: Unit) => {
			if (unit.Id > 0) {
				return updateUnit(unit);
			}
			return createUnit(unit);
		},
		onMutate: () => {
			setSaveStatus('SAVING');
			setCreatedId(null);
		},
		onSuccess: (savedUnit, initalUnit) => {
			if (savedUnit) {
				queryClient.invalidateQueries({ queryKey: [QUERYKEY_UNIT, savedUnit.Id] });
				queryClient.invalidateQueries({ queryKey: [QUERYKEY_ROOTUNITS] });
				setSaveStatus('SAVED');
				if (initalUnit.Id <= 0) {
					setCreatedId(savedUnit.Id);
				}
			} else {
				setSaveStatus('ERROR');
			}
		},
		onError: (err) => {
			console.log('error', err);
			setSaveStatus('ERROR');
		},
	});

	return {
		saveStatus,
		createdId,
		saveUnit: (unit: Unit) => saveUnitMutation.mutate(unit),
	};
};
